import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Typography,
  Divider,
  Tree,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { roleService, Role as RoleType } from '../services/roleService';
import * as menuService from '../services/menuService';

const { Title, Text } = Typography;
const { TextArea } = Input;

// 使用导入的 Role 类型
type Role = RoleType;

const protectedRoleCodes = new Set(['disease_control', 'super_admin', 'admin']);

const isProtectedRole = (role: Role) => {
  const code = String(role.code || '').trim().toLowerCase();
  const name = String(role.name || '').trim();
  return protectedRoleCodes.has(code) || name === '超级管理员';
};

const RoleManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [menuModalVisible, setMenuModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [form] = Form.useForm();

  const [roles, setRoles] = useState<Role[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 菜单数据
  const [menuTree, setMenuTree] = useState<menuService.Menu[]>([]);
  const [allMenus, setAllMenus] = useState<menuService.Menu[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);

  // 加载菜单数据
  const loadMenus = async () => {
    try {
      setMenuLoading(true);
      const [treeRes, allRes] = await Promise.all([
        menuService.getMenuTree(),
        menuService.getAllMenus(),
      ]);
      setMenuTree(treeRes.data || []);
      setAllMenus(allRes.data || []);
    } catch (error) {
      console.error('加载菜单失败:', error);
    } finally {
      setMenuLoading(false);
    }
  };

  // 加载角色列表
  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.list({ page, pageSize });
      setRoles(response.list);
      setTotal(response.total);
    } catch (error) {
      message.error('加载角色列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadRoles();
    loadMenus();
  }, [page, pageSize]);

  // 将菜单树转换为Tree组件需要的格式
  const convertToTreeData = (menus: menuService.Menu[]): any[] => {
    return menus
      // Hidden button entries are operation permissions, not sidebar items.
      // Keep them assignable in role management while leaving ordinary hidden
      // menus out of the permission tree.
      .filter(m => m.status === 1 && (m.visible === 1 || m.type === 3))
      .map(menu => ({
        key: menu.permission || menu.id,
        title: menu.name,
        children: menu.children ? convertToTreeData(menu.children) : undefined,
      }));
  };

  // 打开新增/编辑对话框
  const handleOpenModal = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      form.setFieldsValue(role);
      setCheckedKeys(role.permissions || []);
    } else {
      setEditingRole(null);
      form.resetFields();
      setCheckedKeys([]);
    }
    setModalVisible(true);
  };

  // 关闭对话框
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingRole(null);
    form.resetFields();
    setCheckedKeys([]);
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (checkedKeys.length === 0) {
        message.error('请至少选择一项菜单权限');
        return;
      }
      
      setLoading(true);
      const submitData = {
        ...values,
        permissions: checkedKeys.filter(key => !key.startsWith('menu_')), // 过滤掉目录类型的key
      };

      if (editingRole) {
        await roleService.update(editingRole.id, submitData);
        message.success('角色更新成功');
      } else {
        await roleService.create(submitData);
        message.success('角色创建成功');
      }
      
      handleCloseModal();
      loadRoles();
    } catch (error: any) {
      message.error(error.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除角色
  const handleDelete = async (id: string) => {
    try {
      await roleService.delete(id);
      message.success('角色删除成功');
      loadRoles();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  // 切换角色状态
  const handleToggleStatus = async (role: Role) => {
    try {
      const newStatus = role.status === 'active' ? 'inactive' : 'active';
      await roleService.updateStatus(role.id, newStatus);
      message.success(`角色已${role.status === 'active' ? '禁用' : '启用'}`);
      loadRoles();
    } catch (error: any) {
      message.error(error.message || '状态更新失败');
    }
  };

  // 获取权限对应的菜单名称
  const getMenuNameByPermission = (permission: string): string => {
    const menu = allMenus.find(m => m.permission === permission);
    return menu?.name || permission;
  };

  // 获取角色可访问的菜单列表
  const getRoleMenus = (permissions: string[]) => {
    return permissions.map(p => getMenuNameByPermission(p));
  };

  // 查看角色菜单详情
  const handleViewMenus = (role: Role) => {
    setSelectedRole(role);
    setMenuModalVisible(true);
  };

  // 按父菜单分组权限
  const groupPermissionsByParent = (permissions: string[]) => {
    const groups: Record<string, { name: string; children: string[] }> = {};
    
    permissions.forEach(permission => {
      const menu = allMenus.find(m => m.permission === permission);
      if (menu) {
        const parentMenu = menu.parentId ? allMenus.find(m => m.id === menu.parentId) : null;
        const groupName = parentMenu?.name || '其他';
        
        if (!groups[groupName]) {
          groups[groupName] = { name: groupName, children: [] };
        }
        groups[groupName].children.push(menu.name);
      }
    });
    
    return groups;
  };

  // 表格列配置
  const columns = [
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text: string) => (
        <Space>
          <SafetyOutlined style={{ color: '#1890ff' }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: '角色代码',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '可访问菜单',
      dataIndex: 'permissions',
      key: 'menus',
      width: 300,
      render: (permissions: string[]) => {
        const menus = getRoleMenus(permissions);
        return (
          <Space wrap size={[0, 4]}>
            {menus.slice(0, 3).map((menu, index) => (
              <Tag key={index} color="blue">{menu}</Tag>
            ))}
            {menus.length > 3 && (
              <Tag color="default">+{menus.length - 3} 项</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '权限数量',
      dataIndex: 'permissions',
      key: 'permissionCount',
      width: 100,
      align: 'center' as const,
      render: (permissions: string[]) => (
        <Tag color="green">{permissions.length} 项</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: Role) => {
        const protectedRole = isProtectedRole(record);
        return (
          <Space>
            <Button type="link" size="small" onClick={() => handleViewMenus(record)}>
              查看菜单
            </Button>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenModal(record)}>
              编辑
            </Button>
            {!protectedRole ? (
              <>
                <Button type="link" size="small" onClick={() => handleToggleStatus(record)}>
                  {record.status === 'active' ? '禁用' : '启用'}
                </Button>
                <Popconfirm
                  title="确定要删除这个角色吗？"
                  description="删除后将无法恢复，且该角色下的用户将失去权限。"
                  onConfirm={() => handleDelete(record.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              </>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <SafetyOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0 }}>角色管理</Title>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
            新增角色
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <Modal
        title={editingRole ? '编辑角色' : '新增角色'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        width={700}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="角色名称"
            rules={[
              { required: true, message: '请输入角色名称' },
              { max: 50, message: '角色名称不能超过50个字符' },
            ]}
          >
            <Input placeholder="请输入角色名称" />
          </Form.Item>

          <Form.Item
            name="code"
            label="角色代码"
            rules={[
              { required: true, message: '请输入角色代码' },
              { pattern: /^[a-z_]+$/, message: '只能包含小写字母和下划线' },
              { max: 50, message: '角色代码不能超过50个字符' },
            ]}
          >
            <Input placeholder="例如: custom_role" disabled={!!editingRole} />
          </Form.Item>

          <Form.Item
            name="description"
            label="角色描述"
            rules={[{ max: 200, message: '描述不能超过200个字符' }]}
          >
            <TextArea rows={3} placeholder="请输入角色描述" />
          </Form.Item>

          <Form.Item label="菜单权限配置" required extra="勾选该角色可以访问的菜单项">
            {menuLoading ? (
              <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
            ) : (
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12, maxHeight: 400, overflow: 'auto' }}>
                <Tree
                  checkable
                  checkedKeys={checkedKeys}
                  onCheck={(checked) => setCheckedKeys(checked as string[])}
                  treeData={convertToTreeData(menuTree)}
                  defaultExpandAll
                />
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看角色菜单对话框 */}
      <Modal
        title={
          <Space>
            <SafetyOutlined style={{ color: '#1890ff' }} />
            <span>{selectedRole?.name} - 可访问菜单</span>
          </Space>
        }
        open={menuModalVisible}
        onCancel={() => setMenuModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setMenuModalVisible(false)}>关闭</Button>,
        ]}
        width={600}
      >
        {selectedRole && (
          <div>
            <Space orientation="vertical" style={{ width: '100%' }} size="large">
              {Object.entries(groupPermissionsByParent(selectedRole.permissions)).map(([group, data]) => (
                <Card key={group} size="small" title={data.name}>
                  <Space wrap>
                    {data.children.map((menuName, index) => (
                      <Tag key={index} color="blue">{menuName}</Tag>
                    ))}
                  </Space>
                </Card>
              ))}
            </Space>
            
            <Divider />
            
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">共 {selectedRole.permissions.length} 个菜单权限</Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RoleManagement;
